import { useMemo, useState } from 'react';
import type {
  MoveAttachmentsJob,
  MoveAttachmentsPhase,
  MoveAttachmentsStatus,
  SourceSystem,
} from '@atlas/shared';
import { SOURCE_SYSTEM_LABELS } from '@atlas/shared';
import { formatBytes } from '../lib/redshiftExports.js';
import { moveAttachmentsWrittenDestinationUris } from '../lib/moveAttachments.js';
import { MoveAttachmentsDetailModal } from './MoveAttachmentsDetailModal.js';
import { SourceBadge } from './SourceBadge.js';

interface MoveAttachmentsTableProps {
  jobs: Array<MoveAttachmentsJob & { id: string }>;
  loading?: boolean;
}

const PHASE_LABELS: Record<MoveAttachmentsPhase, string> = {
  extracting: 'Extracting',
  discovering: 'Checking export',
  planning: 'Dry run',
  copying: 'Copying',
  verifying: 'Checking destination',
};

const STATUS_LABELS: Record<MoveAttachmentsStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  awaiting_confirm: 'Ready to copy',
  complete: 'Complete',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const STATUS_STYLES: Record<MoveAttachmentsStatus, string> = {
  queued: 'bg-[#f1f1ef] text-[#73726e]',
  running: 'bg-[#e7f0fb] text-[#2383e2]',
  awaiting_confirm: 'bg-[#fbf3db] text-[#b5820b]',
  complete: 'bg-[#e5f5f2] text-[#0f7b6c]',
  failed: 'bg-[#fbe8e8] text-[#e03e3e]',
  cancelled: 'bg-[#f1f1ef] text-[#aeaaa4]',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type CopyKind = 'documents' | 'images' | 'both';

const COPY_TAGS: Record<CopyKind, { label: string; className: string }> = {
  documents: { label: 'Documents', className: 'bg-[#f1f1ef] text-[#73726e]' },
  images: { label: 'Images', className: 'bg-[#e7f0fb] text-[#2383e2]' },
  both: { label: 'Both', className: 'bg-[#f4f0f8] text-[#9065b0]' },
};

function copyKind(job: MoveAttachmentsJob): CopyKind {
  if (job.imagesOnly) return 'images';
  if (job.includeImages) return 'both';
  return 'documents';
}

function countLabel(count: number, bytes: number): string {
  return `${count.toLocaleString('en-GB')} · ${formatBytes(bytes)}`;
}

function filesLabel(job: MoveAttachmentsJob): string {
  if (job.status === 'running' && job.progress) {
    return `${job.progress.copied.toLocaleString('en-GB')} / ${job.progress.total.toLocaleString('en-GB')}`;
  }
  const kind = copyKind(job);
  if (kind === 'images') {
    if (!job.imagePlan) return '—';
    return countLabel(job.imagePlan.matching, job.imagePlan.matchingBytes);
  }
  if (!job.plan) return '—';
  const documents = countLabel(job.plan.matching, job.plan.matchingBytes);
  if (kind === 'both' && job.imagePlan) {
    return `${documents} + ${countLabel(job.imagePlan.matching, job.imagePlan.matchingBytes)} images`;
  }
  return documents;
}

export function MoveAttachmentsTable({ jobs, loading }: MoveAttachmentsTableProps) {
  const [detail, setDetail] = useState<(MoveAttachmentsJob & { id: string }) | null>(null);
  const [search, setSearch] = useState('');
  const open = detail ? jobs.find((job) => job.id === detail.id) ?? detail : null;

  const needle = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!needle) return jobs;
    return jobs.filter((job) =>
      [
        job.firm,
        job.sourceSystem,
        SOURCE_SYSTEM_LABELS[job.sourceSystem as SourceSystem] ?? job.sourceSystem,
        job.sourcePath,
        job.createdBy?.displayName,
      ].some((value) => typeof value === 'string' && value.toLowerCase().includes(needle)),
    );
  }, [jobs, needle]);

  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-3">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by agency, source, or dry run by…"
          className="w-full sm:w-96 px-3 py-1.5 text-sm border border-[#e9e9e7] rounded-md bg-white text-[#37352f] placeholder:text-[#aeaaa4] focus:outline-none focus:border-[#2383e2]"
        />
        {needle && (
          <span className="shrink-0 text-xs text-[#73726e] tabular-nums">
            {filtered.length} of {jobs.length}
          </span>
        )}
      </div>
      <div className="border border-[#e9e9e7] rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#fafafa] text-xs text-[#73726e]">
            <tr>
              <th className="text-left font-medium px-3 py-2">Agency</th>
              <th className="text-left font-medium px-3 py-2">Source</th>
              <th className="text-left font-medium px-3 py-2">Exports</th>
              <th className="text-left font-medium px-3 py-2">Status</th>
              <th className="text-left font-medium px-3 py-2">Files</th>
              <th className="text-left font-medium px-3 py-2">Started</th>
            </tr>
          </thead>
          <tbody>
            {loading && jobs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-[#aeaaa4]">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && jobs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-[#aeaaa4]">
                  No attachment moves yet.
                </td>
              </tr>
            )}
            {!loading && jobs.length > 0 && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-[#aeaaa4]">
                  No runs match “{search.trim()}”.
                </td>
              </tr>
            )}
            {filtered.map((job) => {
              const label =
                job.status === 'running' && job.phase
                  ? PHASE_LABELS[job.phase]
                  : STATUS_LABELS[job.status];
              const destinations = moveAttachmentsWrittenDestinationUris(job);
              return (
                <tr
                  key={job.id}
                  onClick={() => setDetail(job)}
                  className="border-t border-[#f1f1ef] cursor-pointer hover:bg-[#fafafa]"
                >
                  <td className="px-3 py-2 align-top">
                    <div>
                      <span className="font-mono text-[#37352f]">{job.firm}</span>
                      <span
                        className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${COPY_TAGS[copyKind(job)].className}`}
                      >
                        {COPY_TAGS[copyKind(job)].label}
                      </span>
                    </div>
                    {destinations.length > 0 && (
                      <div className="mt-0.5 flex flex-col gap-0.5">
                        {destinations.map((uri) => (
                          <span
                            key={uri}
                            className="font-mono text-[11px] leading-4 text-[#aeaaa4] break-all"
                          >
                            {uri}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <SourceBadge system={job.sourceSystem} />
                  </td>
                  <td className="px-3 py-2 tabular-nums text-[#37352f]">
                    {job.unzippedPrefixes ? job.unzippedPrefixes.length.toLocaleString('en-GB') : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[job.status]}`}
                    >
                      {job.status === 'running' && (
                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                      )}
                      {label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#37352f] tabular-nums">{filesLabel(job)}</td>
                  <td className="px-3 py-2 text-[#73726e]">{formatDate(job.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {open && (
        <MoveAttachmentsDetailModal
          key={open.id}
          job={open}
          onClose={() => setDetail(null)}
        />
      )}
    </>
  );
}
